import { useEffect, useState } from "react";
import StickersScattered from "src/components/StickersScattered";
import { PaletteWrapper } from "src/contexts/PaletteContext";
import { useSafeArea } from "src/swsh-native";
import CommentsThreadContent from "./CommentsThreadContent";
import CommentsThreadViewedTracker from "./components/CommentsThreadViewedTracker";
import CommentsThreadContext, { CommentsThreadContextType } from "./contexts/CommentsThreadContext";
import { CommentsThreadStoreType, createCommentsThreadStore } from "./stores/commentsThreadStore";

const CommentsThread = ({
	threadSpecifier,
	displayMessageThreads = false,
	overrideSafeArea,
	title = "Thread",
	isAdmin = false,
	onClose,
	onBack,
	onPhotoThreadPress,
	onCreateCommentSuccess,
	isFocused = true,
	palette,
	stickers,
	getUserTagSuggestions,
	testID,
}: {
	threadSpecifier: CommentsThreadStoreType["threadSpecifier"];
	/**
	 * @default false
	 */
	displayMessageThreads?: boolean;
	overrideSafeArea?: {
		top?: number;
		bottom?: number;
	};
	/**
	 * @default "Thread"
	 */
	title?: string;
	/**
	 * @default false
	 */
	isAdmin?: boolean;
	onClose?: () => void;
	onBack?: () => void;
	onPhotoThreadPress?: (props: { photoId: string }) => void;
	onCreateCommentSuccess?: (props: {
		commentId: string;
		threadSpecifier: CommentsThreadStoreType["threadSpecifier"];
	}) => void;
	/**
	 * Whether or not the thread is focused. Used for marking threads as read.
	 * @default true
	 */
	isFocused?: boolean;
	palette?: {
		primary?: string;
		secondary?: string;
		tertiary?: string;
	};
	stickers?: {
		url: string | null | undefined;
	}[];
	getUserTagSuggestions?: CommentsThreadStoreType["getUserTagSuggestions"];
	testID?: string;
}) => {
	const safeArea = useSafeArea();
	const [value] = useState<CommentsThreadContextType>(() => ({
		store: createCommentsThreadStore({
			threadSpecifier,
			displayMessageThreads,
			safeArea: {
				top: overrideSafeArea?.top ?? safeArea.top,
				bottom: overrideSafeArea?.bottom ?? safeArea.bottom,
			},
			title,
			isAdmin,
			onClose,
			onBack,
			getUserTagSuggestions,
		}),
	}));
	useEffect(() => {
		if (onPhotoThreadPress || onCreateCommentSuccess) {
			const { topic } = value.store.getState();
			const unsubscribes: (() => void)[] = [];

			if (onPhotoThreadPress) {
				const { unsubscribe } = topic.on("onPhotoThreadPress", onPhotoThreadPress);
				unsubscribes.push(unsubscribe);
			}
			if (onCreateCommentSuccess) {
				const { unsubscribe } = topic.on("onCreateCommentSuccess", onCreateCommentSuccess);
				unsubscribes.push(unsubscribe);
			}
			return () => {
				for (const unsubscribe of unsubscribes) {
					unsubscribe();
				}
			};
		}
		return;
	}, [value, onPhotoThreadPress, onCreateCommentSuccess]);

	return (
		<CommentsThreadContext.Provider value={value}>
			<PaletteWrapper palette={palette}>
				{!!stickers && stickers.length > 0 && (
					<StickersScattered
						stickers={stickers}
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							opacity: 0.3,
						}}
						sizeFactor={0.2}
						collisionMargin={1.2}
					/>
				)}
				<CommentsThreadContent testID={testID} />
				<CommentsThreadViewedTracker isFocused={isFocused} />
			</PaletteWrapper>
		</CommentsThreadContext.Provider>
	);
};
export default CommentsThread;
